import { inject, injectable } from 'inversify'
import type { IServiceManager } from '@/core/middlewares/types'
import type { Service } from '@/config/types'
import { SERVICE_TYPES, type RegisteredServices } from '@/core/container/types'
import { get, isBound } from '@/core/container/container'
import TmLogicError from '@/core/error/tmLogicError'
import type AppModuleResolverService from '@/services/resolvers/appModuleResolverService'
import type {
  ChannelNumberType,
  ChannelProviderFor,
  SenderChannelProvider,
  SenderChannelProviderClass,
} from '@/services/domain/senderSettings/senderChannelTypes'
import { CHANNEL_TO_MODULE } from '@/services/domain/senderSettings/const'

@injectable()
export class SenderChannelManager implements IServiceManager {
  private channels = new Map<ChannelNumberType, RegisteredServices>()

  private pending = new Map<ChannelNumberType, Promise<SenderChannelProvider>>()

  constructor(
    @inject(SERVICE_TYPES.AppModuleResolverService)
    protected readonly appModuleResolverService: AppModuleResolverService,
  ) {}

  public addService(service: Service): void {
    const provideClass = service.bindingValue as SenderChannelProviderClass
    const numberType = provideClass?.numberType

    if (!numberType) {
      throw new TmLogicError(`${String(service.id)}: sender channel provider must declare static numberType`)
    }

    if (this.channels.has(numberType)) {
      return
    }

    this.channels.set(numberType, service.id as RegisteredServices)
  }

  public resolveProvider<K extends ChannelNumberType>(numberType: K): Promise<ChannelProviderFor<K>> {
    const bound = this.getProviderOrNull(numberType)
    if (bound) {
      return Promise.resolve(bound)
    }

    const existing = this.pending.get(numberType)
    if (existing) {
      return existing as Promise<ChannelProviderFor<K>>
    }

    const pending = this.loadChannel(numberType)
    this.pending.set(numberType, pending)
    return pending as Promise<ChannelProviderFor<K>>
  }

  public getProviderOrNull<K extends ChannelNumberType>(numberType: K): ChannelProviderFor<K> | null {
    const id = this.channels.get(numberType)
    if (!id || !isBound(id)) {
      return null
    }

    return get<ChannelProviderFor<K>>(id)
  }

  private async loadChannel<K extends ChannelNumberType>(numberType: K): Promise<ChannelProviderFor<K>> {
    try {
      await this.appModuleResolverService.resolve({ modules: [CHANNEL_TO_MODULE[numberType]] })
      const id = this.channels.get(numberType)
      if (!id) {
        throw new TmLogicError(
          `SenderChannelProvider for "${numberType}" was not registered after loading module "${CHANNEL_TO_MODULE[numberType]}".`,
        )
      }
      return get<ChannelProviderFor<K>>(id)
    } finally {
      this.pending.delete(numberType)
    }
  }
}
